import { Checkbox, Flex } from 'antd';
import type { CheckboxSemanticType } from 'antd/es/checkbox/Checkbox';
import type { CheckboxGroupProps, CheckboxProps, CheckboxRef } from
  'antd/lib/checkbox';
import clx from 'classnames';
import React from 'react';

import { css } from 'config/emotionCss';
import colors from 'config/theme/colors';

import { Tooltip } from '.';

/**
 * Inline rather than a class: antd's `.ant-checkbox-disabled + span` weighs 0-2-0, which a semantic
 * class cannot outrank.
 */
const disabledLabelStyle = (isBlocked: boolean) => (isBlocked ? { color: colors['@font-grey-light'] } : undefined);

/* Per instance rather than in the ConfigProvider: through the config this also reached CheckboxCards,
   whose cards need the label to fill the box instead of centring a row. */
const labelStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

type FBCheckboxProps = CheckboxProps & {
  tooltip?: React.ReactNode;
  disableHelper?: React.ReactNode;
};

type FBCheckboxType = React.ForwardRefExoticComponent<
  FBCheckboxProps & React.RefAttributes<CheckboxRef>
>;

const FBCheckbox = React.forwardRef<CheckboxRef, FBCheckboxProps>((
  { className, tooltip, disableHelper, disabled, styles, classNames, ...rest },
  ref
) => {
  /* Resolved at call time and concatenated per key, so a caller returning its own `label` from the
     function form adds to labelStyle instead of replacing it. */
  const mergeLabel = (inherited?: CheckboxSemanticType['classNames']) => ({
    ...inherited,
    label: clx(labelStyle, inherited?.label),
  });

  const mergedClassNames = typeof classNames === 'function'
    ? (info: Parameters<typeof classNames>[0]) => mergeLabel(classNames(info))
    : mergeLabel(classNames);

  return (
    <Flex align="center">
      <Tooltip placement="top" title={disabled && disableHelper}>
        <Checkbox
          ref={ref}
          disabled={disabled}
          className={className}
          classNames={mergedClassNames}
          styles={disabled ? { label: disabledLabelStyle(Boolean(disableHelper)), ...styles } : styles}
          {...rest}
        />
      </Tooltip>
      {tooltip && (
        <Tooltip title={tooltip}>
          <span className="fb-icon--info text-base text-secondary" />
        </Tooltip>
      )}
    </Flex>
  );
}) as FBCheckboxType;

const Group = ({ className, children, ...rest }: CheckboxGroupProps) => (
  <Checkbox.Group className={className} {...rest}>
    {children}
  </Checkbox.Group>
);

export default Object.assign(FBCheckbox, { Group });
